import React, { useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Grid, Stack, Divider,
  Dialog, DialogTitle, DialogContent, IconButton,
} from "@mui/material";
import { BsWhatsapp } from "react-icons/bs";
import { FiLink2, FiPause, FiPlay, FiPhone, FiRefreshCw, FiX, FiSmartphone, FiPlusCircle } from "react-icons/fi";
import { EmptyState, ConfirmDialog, InfoTile, useTokens } from "./shared";

// ---- Popup: choose which Embedded Signup flow to start ----
export function ConnectWhatsAppDialog({ open, loading, onClose, onSelect }) {
  const tokens = useTokens();
  const options = [
    {
      existing: true,
      icon: <FiSmartphone size={22} />,
      title: "Use my existing WhatsApp",
      text: "Keep using your WhatsApp Business app number. Chats stay on your phone and in the CRM.",
    },
    {
      existing: false,
      icon: <FiPlusCircle size={22} />,
      title: "Register a new number",
      text: "Set up a fresh number (or WhatsApp Business account) dedicated to the CRM.",
    },
  ];
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: tokens.surface, backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: tokens.ink, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Connect WhatsApp
        <IconButton size="small" onClick={onClose} disabled={loading}><FiX /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Typography variant="body2" sx={{ color: tokens.inkMuted, mb: 2 }}>
          Choose how you want to connect. A Meta window will open to finish setup.
        </Typography>
        <Stack spacing={1.5}>
          {options.map((o) => (
            <Box
              key={o.title}
              onClick={() => !loading && onSelect(o.existing)}
              sx={{
                display: "flex", gap: 2, p: 2, borderRadius: 2.5, cursor: loading ? "not-allowed" : "pointer",
                border: `1px solid ${tokens.border}`, opacity: loading ? 0.6 : 1,
                transition: "all .15s ease",
                "&:hover": loading ? {} : { borderColor: tokens.wa, bgcolor: tokens.waLight },
              }}
            >
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: tokens.wa, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : o.icon}
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink }}>{o.title}</Typography>
                <Typography variant="caption" sx={{ color: tokens.inkMuted }}>{o.text}</Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export function WhatsAppActions({ wa, onConnect }) {
  const tokens = useTokens();
  if (wa.waLoading) return null;
  return wa.waConnected ? (
    <Button
      variant="outlined"
      onClick={wa.handleWaReconnect}
      disabled={wa.waActionLoading}
      startIcon={<FiRefreshCw size={16} />}
      sx={{ color: tokens.ink, borderColor: tokens.border, textTransform: "none", fontWeight: 700, borderRadius: 2 }}
    >
      Reconnect
    </Button>
  ) : (
    <Button
      variant="contained"
      onClick={onConnect}
      disabled={wa.waActionLoading}
      startIcon={wa.waActionLoading ? <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} /> : <FiLink2 size={16} />}
      sx={{ bgcolor: tokens.wa, "&:hover": { bgcolor: tokens.waDark }, textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none" }}
    >
      Connect WhatsApp
    </Button>
  );
}

export function WhatsAppOverviewTab({ wa, onConnect }) {
  const tokens = useTokens();
  const { waStatus, waLoading, waConnected, waActionLoading, handleWaToggle, handleWaDisconnect } = wa;
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (waLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} thickness={4} sx={{ color: tokens.wa }} />
      </Box>
    );
  }
  if (!waConnected) {
    return (
      <Stack alignItems="center">
        <EmptyState
          icon={<BsWhatsapp size={20} color={tokens.inkFaint} />}
          title="WhatsApp not connected yet"
          caption="Connect your business WhatsApp to automatically message your leads when they come in."
        />
        <Button
          variant="contained"
          onClick={onConnect}
          disabled={waActionLoading}
          startIcon={<FiLink2 size={16} />}
          sx={{ bgcolor: tokens.wa, "&:hover": { bgcolor: tokens.waDark }, textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none" }}
        >
          Connect WhatsApp
        </Button>
      </Stack>
    );
  }
  const enabled = waStatus.isEnabled;
  return (
    <>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}><InfoTile label="Business" value={waStatus.businessName || "—"} /></Grid>
        <Grid item xs={12} sm={4}>
          <InfoTile label="WhatsApp" value={waStatus.displayPhoneNumber || "—"} icon={<FiPhone size={14} color={tokens.inkFaint} />} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InfoTile
            label="Connected"
            value={waStatus.connectedAt ? new Date(waStatus.connectedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2,
          p: 2, borderRadius: 2.5, bgcolor: enabled ? tokens.waLight : tokens.warningBg,
          border: `1px solid ${enabled ? "rgba(37,211,102,0.25)" : "rgba(245,158,11,0.25)"}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: enabled ? tokens.wa : tokens.warning }} />
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink }}>
              Automation: <span style={{ color: enabled ? tokens.wa : tokens.warning }}>{enabled ? "Active" : "Paused"}</span>
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
              {enabled ? "New leads are messaged automatically." : "New leads are not being messaged automatically."}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined" size="small" onClick={handleWaToggle} disabled={waActionLoading}
          startIcon={waActionLoading ? <CircularProgress size={14} thickness={5} /> : enabled ? <FiPause size={14} /> : <FiPlay size={14} />}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, color: enabled ? tokens.inkMuted : tokens.wa, borderColor: enabled ? tokens.border : tokens.wa }}
        >
          {enabled ? "Pause" : "Resume"}
        </Button>
      </Box>

      <Divider sx={{ my: 3, borderColor: tokens.border }} />

      {/* Danger zone */}
      <Box sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${tokens.danger}40`, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink }}>Disconnect WhatsApp</Typography>
          <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
            Stops automated messages and removes access to the inboxes until you reconnect.
          </Typography>
        </Box>
        <Button
          variant="outlined" color="error" size="small" onClick={() => setConfirmOpen(true)} disabled={waActionLoading}
          startIcon={<FiX size={14} />}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
        >
          Disconnect
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Disconnect WhatsApp?"
        message={`This will disconnect ${waStatus.displayPhoneNumber || "your WhatsApp number"}. Automation will stop and you can reconnect at any time.`}
        confirmLabel="Disconnect"
        loading={waActionLoading}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await handleWaDisconnect();
          setConfirmOpen(false);
        }}
      />
    </>
  );
}
